import { TAction, TBoundAction } from '../../actions/types/action';
import { FilterTypes, SearchFiltersType, TSearchAdvancedResults } from '../types';
import { LOCATION_CHANGE } from '../../../libs/router/redux-store';

export type TSetTimezonePayload = {
  timezone: string;
};

export type TSetSearchResultsPayload = {
  results: any;
  searchType: string;
  query: any;
};

export type TPaginationPayload = {
  page: number;
  searchType: string;
  isUserAction?: boolean;
};

export enum GlobalSearchActionTypes {
  SET_PAGINATION_LIMIT = 'GLOBAL_SEARCH/SET_PAGINATION_LIMIT',
  RESET_PAGINATION = 'GLOBAL_SEARCH/RESET_PAGINATION',
  PAGINATE = 'GLOBAL_SEARCH/PAGINATE',
  SET_SEARCH_RESULTS = 'GLOBAL_SEARCH/SET_SEARCH_RESULTS',
  SEARCH_FAILURE = 'GLOBAL_SEARCH/SEARCH_FAILURE',
  SET_SEARCH_TYPE = 'GLOBAL_SEARCH/SET_SEARCH_TYPE',
  START_SEARCH = 'GLOBAL_SEARCH/START_SEARCH',
  ADD_FILTER = 'GLOBAL_SEARCH/ADD_FILTER',
  SET_FILTERS = 'GLOBAL_SEARCH/SET_FILTERS',
  REMOVE_FILTER = 'GLOBAL_SEARCH/REMOVE_FILTER',
  UPDATE_FILTER = 'GLOBAL_SEARCH/UPDATE_FILTER',
  RESET_FILTERS = 'GLOBAL_SEARCH/RESET_FILTERS',
  SET_LAST_SEARCH = 'GLOBAL_SEARCH/SET_LAST_SEARCH',
  SET_SORTED_BY = 'GLOBAL_SEARCH/SET_SORTED_BY',

  SET_TIMEZONE = 'GLOBAL_SEARCH/SET_TIMEZONE',

  FETCH_DYNAMIC_BY_PARAMS = 'GLOBAL_SEARCH/FETCH_DYNAMIC_BY_PARAMS',
  SET_DYNAMIC_BY_PARAMS = 'GLOBAL_SEARCH/SET_DYNAMIC_BY_PARAMS'
}

export type TSetTimezoneMethod = (timezone: string) => TAction<TSetTimezonePayload>;

export type TFetchDynamicByParamsPayload = {
  params: any;
};

export type TSetDynamicByParamsPayload = {
  params: any;
  state: TGlobalSearchDynamicState;
};

export type TSetSearchFailurePayload = {
  results: any[];
  searchType: string;
};

export type TSetPaginationLimitPayload = {
  limit: number;
  searchType: string;
};

export type TResetPaginationPayload = {};

export type TSetSearchTypePayload = {
  selectedType: string;
};

export type TAddFilterPayload = {
  resultType: string;
  filterType: FilterTypes;
  filterPath: string;
  value: any;
};

export type TSetFiltersPayload = {
  resultType: string;
  filters: TFilterParams[];
  sorted: any[];
};

export type TResetFiltersPayload = {
  resultType: string;
};

export type TRemoveFilterPayload = {
  resultType: string;
  id: number;
};

export type TUpdateFilterPayload = {
  resultType: string;
  id: number;
  newKey: string;
  newValue: any;
  filterType: FilterTypes;
};

export type TStartSearchPayload = {
  type: string;
  searchTerm: string;
};

export type TSetLastSearchPayload = {
  search: string;
};

export type TSetSortedByPayload = {
  resultType: string;
  sorted: any;
  page: number;
};

export type TFilterParams = {
  type: FilterTypes;
  path: string;
  optionId?: number;
  value: any;
};

export type TGlobalSearchActions =
  | TBoundAction<GlobalSearchActionTypes.SET_SEARCH_RESULTS, TSetSearchResultsPayload>
  | TBoundAction<GlobalSearchActionTypes.SEARCH_FAILURE, TSetSearchFailurePayload>
  | TBoundAction<GlobalSearchActionTypes.SET_SEARCH_TYPE, TSetSearchTypePayload>
  | TBoundAction<GlobalSearchActionTypes.ADD_FILTER, TAddFilterPayload>
  | TBoundAction<GlobalSearchActionTypes.SET_FILTERS, TSetFiltersPayload>
  | TBoundAction<GlobalSearchActionTypes.RESET_FILTERS, TResetFiltersPayload>
  | TBoundAction<GlobalSearchActionTypes.REMOVE_FILTER, TRemoveFilterPayload>
  | TBoundAction<GlobalSearchActionTypes.UPDATE_FILTER, TUpdateFilterPayload>
  | TBoundAction<GlobalSearchActionTypes.START_SEARCH, TStartSearchPayload>
  | TBoundAction<GlobalSearchActionTypes.SET_LAST_SEARCH, TSetLastSearchPayload>
  | TBoundAction<GlobalSearchActionTypes.SET_SORTED_BY, TSetSortedByPayload>
  | TBoundAction<typeof LOCATION_CHANGE, any>
  | TBoundAction<GlobalSearchActionTypes.PAGINATE, TPaginationPayload>
  | TBoundAction<GlobalSearchActionTypes.SET_PAGINATION_LIMIT, TSetPaginationLimitPayload>
  | TBoundAction<GlobalSearchActionTypes.SET_TIMEZONE, TSetTimezonePayload>
  | TBoundAction<GlobalSearchActionTypes.RESET_PAGINATION, TResetPaginationPayload>
  | TBoundAction<GlobalSearchActionTypes.FETCH_DYNAMIC_BY_PARAMS, TFetchDynamicByParamsPayload>
  | TBoundAction<GlobalSearchActionTypes.SET_DYNAMIC_BY_PARAMS, TSetDynamicByParamsPayload>;
// Types shared by components

export type TAddFilterMethod = (
  resultType: string,
  filterType: FilterTypes,
  filterPath: string,
  value: any
) => TAction<TAddFilterPayload>;

export type TSetFiltersMethod = (
  resultType: string,
  filters: TFilterParams[],
  sorted: any[]
) => TAction<TSetFiltersPayload>;

export type TUpdateFilterMethod = (
  resultType: string,
  id: number,
  newKey: string,
  newValue: any,
  filterType: FilterTypes
) => TAction<TUpdateFilterPayload>;

export type TRemoveFilterMethod = (resultType: string, id: number) => TAction<TRemoveFilterPayload>;

export type TResetFiltersMethod = (resultType: string) => TAction<TResetFiltersPayload>;

export type TSetSearchTypeMethod = (selectedType: SearchFiltersType) => TAction<TSetSearchTypePayload>;
export type TPaginateResultsMethod = (
  page: number,
  type: string,
  isUserAction?: boolean
) => TAction<TPaginationPayload>;
export type TSetPaginationLimit = (limit: number, searchType: string) => TAction<TSetPaginationLimitPayload>;
export type TResetPagination = () => TAction<TResetPaginationPayload>;

export type TSetSearchResultsMethod = (
  results: any,
  searchType: string,
  query: any
) => TBoundAction<GlobalSearchActionTypes.SET_SEARCH_RESULTS, TSetSearchResultsPayload>;

export type TSetSearchFailureMethod = (results: any[], searchType: string) => TAction<TSetSearchFailurePayload>;

export type TStartSearchMethod = (type: string, term: string) => TAction<TStartSearchPayload>;

export type TSetLastSearchMethod = (search: string) => TAction<TSetLastSearchPayload>;

export type TSetSortedByMethod = (resultType: string, sorted: any) => TAction<TSetSortedByPayload>;

export type TFetchDynamicByParamsMethod = (params: any) => TAction<TFetchDynamicByParamsPayload>;
export type TSetDynamicStateByParamsMethod = (
  params: Record<string, any>,
  state: TGlobalSearchDynamicState
) => TBoundAction<GlobalSearchActionTypes.SET_DYNAMIC_BY_PARAMS, TSetDynamicByParamsPayload>;

export type TGlobalSearch = {
  addFilter: TAddFilterMethod;
  setFilters: TSetFiltersMethod;
  resetFilters: TResetFiltersMethod;
  removeFilter: TRemoveFilterMethod;
  updateFilter: TUpdateFilterMethod;
  setSearchResults: TSetSearchResultsMethod;
  paginate: TPaginateResultsMethod;
  setPaginationLimit: TSetPaginationLimit;
  resetPagination: TResetPagination;
  setSearchFailure: TSetSearchFailureMethod;
  setSearchType: TSetSearchTypeMethod;
  startSearch: TStartSearchMethod;
  setLastSearch: TSetLastSearchMethod;
  setSortedBy: TSetSortedByMethod;
  setTimezone: TSetTimezoneMethod;
  fetchDynamicByParams: TFetchDynamicByParamsMethod;
  setDynamicStateByParams: TSetDynamicStateByParamsMethod;
};

export type TGlobalSearchDynamicState = {
  data: TSearchAdvancedResults | null;
  loading: boolean;
  loaded: boolean;
  error: unknown | null;
};

/*
 * GLOBAL SEARCH STATE
 */
export type TGlobalSearchClientState = {
  loading: boolean;
  loaded: boolean;
  error: boolean;
  searchTerm: string;
  selectedType?: string;
  timezone: string | null;
  character: TGlobalSearchSubtypeState;
  guild: TGlobalSearchSubtypeState;
  dynamic: Record<string, TGlobalSearchDynamicState>;
  [key: string]: any;
};

export type TGlobalSearchSubtypeState = {
  page: number;
  results: TSearchAdvancedResults;
  sortField: string;
  filters: any;
  filterCount: number;
  sorted: any;
  query: any;
};
